import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPass, setShowPass] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email.trim(), password);
      toast.success("Berhasil masuk");
      navigate("/");
    } catch (err) {
      if (!err.response) setError(t("login.networkError"));
      else if (err.response.status === 401) setError(t("login.invalidCred"));
      else setError(apiErr(err.response?.data?.detail) || t("login.invalidCred"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary-foreground text-primary flex items-center justify-center font-head font-extrabold text-xl">F</div>
          <span className="font-head font-bold text-xl">Fiskal <span className="font-light opacity-80">By SAVIOR</span></span>
        </div>
        <div>
          <h1 className="font-head text-3xl font-bold leading-tight tracking-tight">
            {t("login.heroTitle")}
          </h1>
          <p className="mt-4 text-primary-foreground/70 text-sm leading-relaxed max-w-md">
            {t("login.heroDesc")}
          </p>
          <div className="mt-8 flex items-center gap-2 text-sm text-primary-foreground/80">
            <ShieldCheck className="h-4 w-4" /> Multi-tenant · Role-based access · Audit trail
          </div>
        </div>
        <p className="text-xs text-primary-foreground/50">© 2026 Fiskal By SAVIOR</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 justify-center mb-2">
            <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-head font-extrabold">F</div>
            <span className="font-head font-bold text-lg">Fiskal By SAVIOR</span>
          </div>
          <div>
            <h2 className="font-head text-2xl font-bold tracking-tight">{t("login.title")}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t("login.subtitle")}</p>
          </div>
          {error && (
            <div className="text-sm rounded-md border border-destructive/40 bg-destructive/10 text-destructive px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email">{t("login.email")}</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" placeholder="nama@perusahaan.id" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t("login.password")}</Label>
            <div className="relative">
              <Input id="password" type={showPass ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" placeholder="••••••••" className="pr-10" />
              <button type="button" onClick={() => setShowPass((v) => !v)} aria-label={showPass ? t("login.hidePassword") : t("login.showPassword")} title={showPass ? t("login.hidePassword") : t("login.showPassword")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" data-testid="toggle-password">
                {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {t("login.submit")}
          </Button>
        </form>
      </div>
    </div>
  );
}
